import { memo, useEffect } from 'react';
import { useRouter } from 'next/router';
import { Common } from '@stockbit/ui';

import {
  ContainerWrapper,
  ContentWrapper,
  MenuWrapper,
} from 'global/GlossaryLayout/Wrapper';
import {
  getLetterParameters,
  isValidGlossary,
  isValidTerms,
} from 'utils/glossary';

import { useGlossaries, useGlossary } from './hooks';

import useStore from './store';
import Search from './Search/Search';
import MenuByLetter from './Menu/MenuByLetter';
import MenuByTerm from './Menu/MenuByTerm';
import PreviewPane from './PreviewPane/Preview';
import { DATA_CY_GLOSSARY } from './__tests__/constant';

const { Text } = Common;

function GlossaryFeature(): JSX.Element {
  const router = useRouter();
  const {
    glossaries,
    glossary,
    setGlossaries,
    setGlossary,
  } = useStore();

  const { letter, slug } = getLetterParameters(router?.query);
  const selectedLetter: string = letter === '#' ? '1' : letter;

  const { data: dataTerms, isLoading } = useGlossaries(letter);
  const { data: dataItem, isLoading: isLoadingItem } = useGlossary(slug);

  useEffect(() => {
    if (!dataTerms) return;

    setGlossaries(dataTerms);
  }, [dataTerms]);

  useEffect(() => {
    const setGlossaryData = (): void => {
      if (isValidGlossary(dataItem)) {
        setGlossary(dataItem);
        return;
      }

      if (!isValidTerms(dataTerms)) {
        return;
      }

      const [firstData] = dataTerms;
      if (isValidGlossary(firstData)) {
        setGlossary(firstData);
      }
    };

    setGlossaryData();
  }, [dataItem, dataTerms]);

  return (
    <ContainerWrapper data-cy={DATA_CY_GLOSSARY}>
      <ContentWrapper>
        <MenuWrapper as="header">
          <Text family="bold" size="16px" height="40px" as="h1">
            Glossary
          </Text>
          <Search />
          <MenuByLetter selectedLetter={selectedLetter} />
        </MenuWrapper>
        <MenuWrapper role="navigation">
          <MenuByTerm
            terms={glossaries}
            slug={slug}
            loading={isLoading}
          />
        </MenuWrapper>
      </ContentWrapper>

      <ContentWrapper as="section">
        <PreviewPane
          name={glossary?.name}
          definition={glossary?.definition}
          formula={glossary?.formula}
          explanation={glossary?.explanation}
          loading={!glossary || (!!slug && isLoadingItem)}
        />
      </ContentWrapper>
    </ContainerWrapper>
  );
}

export default memo(GlossaryFeature);
